'use client';

import {
	BASE_PRECISION_EXP,
	isSignedMsgOrder,
	Order,
	OrderActionExplanation,
	PositionDirection,
	QUOTE_PRECISION_EXP,
	BigNum,
	MarketType,
} from '@velocity-exchange/sdk';
import {
	ENUM_UTILS,
	Opaque,
	Serializer,
	UIMarket,
	UISerializableOrderActionRecord,
	abbreviateAddress,
	getVelocityEventKey,
	matchEnum,
	calculateAverageEntryPrice,
} from '@velocity-exchange/common';
import { PublicKey } from '@solana/web3.js';
import { memo, useCallback, useState } from 'react';
import Env from 'src/environmentVariables/EnvironmentVariables';
import useVelocityClientIsReady from 'src/hooks/useVelocityClientIsReady';
import useVelocityAccountStore from 'src/stores/useVelocityAccountsStore';
import useDevSwitchIsOn from '../hooks/useDevSwitchIsOn';
import useVelocityStore from '../stores/VelocityStore/useVelocityStore';
import useMarketsInfoStore from '../stores/useMarketsInfoStore';
import { formatText } from '@velocity-exchange/common/format';
import RecentTradesDevTooltip from './DevTooltip/RecentTradesDevTooltip';
import InlineLoadingBar from './InlineLoadingBar/InlineLoadingBar';
import Tooltip from 'src/components/primitives/Tooltip/Tooltip';
import Counterparty from './Counterparty';
import ReactDOM from 'react-dom';
import useDevStore from '../stores/useDevStore';
import CopyButton from './Utils/CopyButton';
import { Close } from '@velocity-exchange/icons';
import { useMarketStepSize } from 'src/hooks/useMarketStepSize';
import { cj, cm } from 'src/utils/classNames';

interface RecentTradeRowProps {
	fill: UISerializableOrderActionRecord;
	highlightTrade: boolean;
	isPos: boolean;
	copyAndSnapToTrade: (fill: UISerializableOrderActionRecord) => void;
	copyTrade: (fill: UISerializableOrderActionRecord) => void;
	baseDecimals: number;
	quoteDecimals: number;
	devSwitch?: boolean;
	setShowTradeInfo: (trade?: UISerializableOrderActionRecord) => void;
}

type RecordKey = Opaque<string, 'RecordKey'>;

const getRecordKey = (record: UISerializableOrderActionRecord): RecordKey => {
	return `${record?.fillRecordId?.toString() ?? ''}_${
		record?.takerOrderId?.toString() ?? ''
	}_${record?.makerOrderId?.toString() ?? ''}` as RecordKey;
};

const RecentTradeRowMemo = memo(function RecentTradeRow({
	fill,
	highlightTrade,
	isPos,
	copyAndSnapToTrade,
	copyTrade,
	devSwitch,
	baseDecimals,
	quoteDecimals,
	setShowTradeInfo,
}: RecentTradeRowProps) {
	const entryPrice = calculateAverageEntryPrice(
		fill.quoteAssetAmountFilled ?? BigNum.zero(QUOTE_PRECISION_EXP),
		fill.baseAssetAmountFilled ?? BigNum.zero(BASE_PRECISION_EXP)
	);

	const isLiq = ENUM_UTILS.match(
		fill.actionExplanation,
		OrderActionExplanation.LIQUIDATION
	);

	const [toggleIsOn, setToggleIsOn] = useState(false);

	const shownTradeInfo = useDevStore((s) => s.showInfoForTrade);
	const setDevStore = useDevStore((s) => s.set);

	const togglePauseUpdates = (orderKey: RecordKey) => {
		setDevStore((s) => {
			const currentState = s.pausedRecentTradeKeys ?? {};
			if (currentState[orderKey]) {
				delete currentState[orderKey];
			} else {
				currentState[orderKey] = true;
			}
		});
	};

	const isDevMode = useDevSwitchIsOn();
	const isSgnMsgOrder = isSignedMsgOrder(fill as unknown as Order);

	return (
		<div
			className={cm(
				'relative grid grid-cols-3 px-2 py-1 leading-md text-text-secondary',
				'hover:cursor-pointer hover:bg-button-disabled',
				'typo-body-xs',
				highlightTrade && [
					'bg-button-disabled hover:bg-button-disabled dark:bg-button-secondary-bg dark:hover:bg-button-secondary-bg-hover',
				]
			)}
			onClick={() => {
				if (highlightTrade) {
					copyAndSnapToTrade(fill);
				} else {
					copyTrade(fill);
				}
			}}
		>
			<span
				className={cj(
					'inline-flex',
					isPos
						? 'text-text-positive-green-button'
						: 'text-text-negative-red-button'
				)}
			>
				{isLiq && (
					<Tooltip
						decoration="cursor-help"
						content={`${abbreviateAddress(
							fill.taker ?? PublicKey.default
						)} was liquidated by ${abbreviateAddress(
							fill.maker ?? PublicKey.default
						)}`}
					>
						<div className="mr-0.5">💀</div>
					</Tooltip>
				)}
				{entryPrice.toNum().toLocaleString(Env.locale, {
					maximumFractionDigits: quoteDecimals,
					minimumFractionDigits: quoteDecimals,
				})}
			</span>
			<span className="mr-5 text-right">
				{formatText(fill.baseAssetAmountFilled?.toNum() ?? 0, {
					digits: {
						kind: 'decimals',
						decimals: baseDecimals,
						rounding: 'half-ceil',
					},
				})}
			</span>
			<div className="inline-flex justify-end gap-x-1">
				<span className="text-right">
					{fill.ts &&
						new Date(fill.ts.toNumber() * 1000).toLocaleTimeString('en-US', {
							hourCycle: 'h23',
						})}
				</span>
				{isDevMode && (
					<Counterparty
						// TODO : possible to create some kind of recent cache for stringified authorities? To save on toStrings here
						counterparty={fill.maker?.toString() ?? ''}
						marketType={fill.marketType}
						actionExplanation={fill.actionExplanation}
						isGenericTrade
						isSignedMsgTrade={isSgnMsgOrder}
					/>
				)}
			</div>

			{devSwitch && (
				<>
					<span className="absolute -right-1.5">
						<RecentTradesDevTooltip
							isOn={
								(toggleIsOn ||
									(shownTradeInfo &&
										getRecordKey(shownTradeInfo) === getRecordKey(fill))) ??
								false
							}
							onToggle={() => {
								setToggleIsOn(!toggleIsOn);
								togglePauseUpdates(getRecordKey(fill));
							}}
							setShowContent={(value) => {
								setShowTradeInfo(value || toggleIsOn ? fill : undefined);
							}}
						/>
					</span>
				</>
			)}
		</div>
	);
});

const RecentTradesHeaderMemo = memo(function RecentTradesHeader({
	marketName,
}: {
	marketName: string;
}) {
	return (
		<div
			className={cj(
				'grid grid-cols-3 items-center text-center',
				'place-content-center bg-container-bg',
				'border-b border-stroke-divider p-sm typo-body-2xs text-text-tertiary'
			)}
		>
			<span className="text-left">Price (USD) </span>
			<span className="mr-5 pr-1 text-right">Size ({marketName})</span>
			<span className="mr-7 text-right">Time</span>
		</div>
	);
});

const RecentMarketTrades = memo(function RecentTradeMemo(props: {
	className?: string;
}) {
	const devSwitch = useDevSwitchIsOn();
	const velocityClientIsReady = useVelocityClientIsReady();

	const fills = useVelocityStore((s) => s.marketTradeHistory.trades);

	const selectedMarketBaseAssetSymbol = useVelocityStore(
		(s) => s.selectedMarket.current?.uiSymbols.baseAssetSymbol
	);
	const selectedMarketId = useVelocityStore(
		(s) => s.selectedMarket.current?.marketId
	);
	const isLoading = useVelocityStore(
		(s) => s.loadingElements.recentTrades?.isLoading
	);
	const setState = useVelocityStore((s) => s.set);

	const user: PublicKey | undefined = useVelocityAccountStore(
		(s) => s.accounts[s.currentUserKey ?? '']?.pubKey
	);

	const priceDisplayDecimals = useMarketsInfoStore(
		(s) =>
			s.getMarketInfoByIndexAndType(
				selectedMarketId?.marketIndex ?? 0,
				selectedMarketId?.marketType ?? MarketType.PERP
			)?.genericInfo?.priceDisplayDecimals
	);

	// Quote decimals displayed should be number of decimals in min tick size (same as display decimals I believe)
	const quoteDecimals = velocityClientIsReady ? (priceDisplayDecimals ?? 2) : 2;

	// Base decimals displayed should be number of decimals in min step size
	const stepSize = useMarketStepSize(selectedMarketId);
	const baseDecimals = (`${stepSize}`.split('.')[1] ?? '').length;

	const copyTrade = useCallback(
		(fillInfo: UISerializableOrderActionRecord) => {
			setState((s) => {
				const direction = fillInfo.baseAssetAmountFilled?.ltZero()
					? 'sell'
					: 'buy';
				s.tradeForm.side = direction;
				s.tradeForm.leadSide = 'base';
				// @ts-expect-error
				s.tradeForm.baseSizeStringValue =
					fillInfo.baseAssetAmountFilled?.printShort();
			});

			setState((s) => {
				if (s.tradeForm.orderType !== 'market') {
					const avgPrice = calculateAverageEntryPrice(
						fillInfo.quoteAssetAmountFilled ?? BigNum.zero(QUOTE_PRECISION_EXP),
						fillInfo.baseAssetAmountFilled ?? BigNum.zero(BASE_PRECISION_EXP)
					).toTradePrecision();

					// @ts-expect-error
					s.tradeForm.priceBoxStringValue = avgPrice;
				}
			});
		},
		[setState]
	);

	const copyAndSnapToTrade = useCallback(
		(fillInfo: UISerializableOrderActionRecord) => {
			copyTrade(fillInfo);
			setState((s) => {
				s.tradePageTable.currentTab = 'tradeHistory';
				s.tradePageTable.tradeHistoryMarketFilter = new UIMarket(
					fillInfo.marketIndex,
					fillInfo.marketType
				).uiSymbols.marketSymbol;
			});
		},
		[copyTrade, setState]
	);

	const tradeToShow = useDevStore((s) => s.showInfoForTrade);
	const setDevStore = useDevStore((s) => s.set);
	const setShowTradeInfo = (trade?: UISerializableOrderActionRecord) => {
		setDevStore((s) => {
			s.showInfoForTrade = trade;
		});
	};

	return (
		<>
			<RecentTradesHeaderMemo
				marketName={selectedMarketBaseAssetSymbol ?? ''}
			/>

			{isLoading ? (
				<div className="flex h-full w-full items-center justify-center">
					<InlineLoadingBar />
				</div>
			) : (
				fills &&
				!!fills.length && (
					<div
						className={cm(
							'thin-scroll overflow-x-auto overflow-y-scroll bg-container-bg',
							'grow xs:min-h-[25vh] sm:h-0 sm:min-h-0',
							props.className
						)}
					>
						{fills.map((fill, index) => {
							const highlightTrade = !!(
								user &&
								(fill.maker?.equals(user) || fill.taker?.equals(user))
							);

							const isPos = matchEnum(
								fill.takerOrderDirection,
								PositionDirection.LONG
							);

							return (
								<RecentTradeRowMemo
									// if there is somehow a race condition where dupe records make it here, add index to make sure
									// component doesn't get stuck with duplicate key error and it should cleanup near instantly
									key={`${getVelocityEventKey(fill)}_${index}`}
									fill={fill}
									highlightTrade={highlightTrade}
									isPos={isPos}
									copyAndSnapToTrade={copyAndSnapToTrade}
									copyTrade={copyTrade}
									devSwitch={devSwitch}
									quoteDecimals={quoteDecimals}
									baseDecimals={baseDecimals}
									setShowTradeInfo={setShowTradeInfo}
								/>
							);
						})}
					</div>
				)
			)}

			{tradeToShow &&
				ReactDOM.createPortal(
					<div
						style={{
							zIndex: 100,
							position: 'absolute',
							top: 0,
							left: 0,
						}}
					>
						<div className="relative bg-input-bg p-4 break-normal text-text-default">
							<span
								onClick={() => {
									setShowTradeInfo(undefined);
								}}
								className="absolute top-0 right-0 size-6"
							>
								<Close size={24} />
							</span>
							<span>
								<span className="flex flex-col gap-y-xl">
									<div className="grid grid-cols-[auto_minmax(0,1fr)] gap-y-1">
										<span className="mr-4">Base Amount Filled : </span>
										<span>{tradeToShow.baseAssetAmountFilled?.print()}</span>
										<span className="mr-4">Quote Amount Filled : </span>
										<span>{tradeToShow.quoteAssetAmountFilled?.print()}</span>
										<span className="mr-4">Price : </span>
										<span>
											{calculateAverageEntryPrice(
												tradeToShow.quoteAssetAmountFilled ??
													BigNum.zero(QUOTE_PRECISION_EXP),
												tradeToShow.baseAssetAmountFilled ??
													BigNum.zero(BASE_PRECISION_EXP)
											).print()}
										</span>
									</div>
									<div className="grid grid-cols-[auto_minmax(0,1fr)]">
										{Object.entries(
											Serializer.Serialize.UIOrderActionRecord(tradeToShow) ??
												{}
										).map(([key, value]) => {
											return (
												<>
													<span className="mr-4">{key}</span>
													<div className="flex w-full justify-between gap-x-2">
														<span className="max-w-[500px] break-all">
															{/* @ts-ignore */}
															{value}
														</span>
														<CopyButton
															toastMessage={`Copied ${value}`}
															copyValue={value as string}
														/>
													</div>
												</>
											);
										})}
									</div>
								</span>
							</span>
						</div>
					</div>,
					document.body
				)}
		</>
	);
});

export default RecentMarketTrades;
